import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Check, Calendar, Users, Sparkles, Zap, Clock, DollarSign, ArrowRight, Package, Activity, Shield, FileText, Code, Key, Fingerprint, Lock, TrendingUp, Star, Link2, QrCode, Camera, Bell, Download, Mail, Menu, X, Globe, BadgeDollarSign } from 'lucide-react';
import { QRCodeSVG as QRCodeComponent } from 'qrcode.react';
import { getAllPlans, formatPrice } from '../config/pricing';

const SupplierLandingPage = () => {
  const navigate = useNavigate();
  const plans = getAllPlans(false); // Use demo pricing
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Add scroll animation observer
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in-up');
        }
      });
    }, observerOptions);

    // Observe all sections with the class 'animate-on-scroll'
    document.querySelectorAll('.animate-on-scroll').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <style>{`
        /* Smooth scroll animations - subtle and professional */
        .animate-on-scroll {
          opacity: 0;
          transform: translateY(16px);
          transition: opacity 0.6s ease-out, transform 0.6s ease-out;
        }
        
        .animate-fade-in-up {
          opacity: 1;
          transform: translateY(0);
        }

        /* Smooth scroll behavior */
        html {
          scroll-behavior: smooth;
        }
        
        /* Enhanced button hover states */
        .btn-supplier-primary {
          transition: all 0.2s ease;
        }
        .btn-supplier-primary:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.25);
        }
        .btn-supplier-primary:active {
          transform: translateY(0);
        }
      `}</style>
      
      {/* Header/Nav */}
      <header className="border-b border-gray-100 sticky top-0 z-50 bg-white/95 backdrop-blur-sm pt-[env(safe-area-inset-top)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
              <img 
                src="/logo-icon.png" 
                alt="BookingFlows" 
                className="w-10 h-10 rounded-xl object-cover"
              />
              <span className="text-2xl font-bold text-gray-900">BookingFlows</span>
            </div>
            
            {/* Desktop Buttons - Hidden on mobile */}
            <div className="hidden md:flex items-center gap-3">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/login')}
                className="text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              >
                Log In
              </Button>
              <Button 
                onClick={() => navigate('/signup')}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-5 py-2.5 rounded-xl"
              >
                Request Demo
              </Button>
            </div>
            
            {/* Mobile Menu Button - Visible on mobile only */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-gray-700" />
              ) : (
                <Menu className="w-6 h-6 text-gray-700" />
              )}
            </button>
          </div>
          
          {/* Mobile Dropdown Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-4 space-y-2 border-t border-gray-100 pt-4">
              <Button 
                variant="ghost" 
                onClick={() => {
                  navigate('/login');
                  setMobileMenuOpen(false);
                }}
                className="w-full btn-landing-ghost justify-start"
              >
                Log In
              </Button>
              <Button 
                onClick={() => {
                  navigate('/signup');
                  setMobileMenuOpen(false);
                }}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 rounded-lg"
              >
                Request Demo
              </Button>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto container-px section-py">
        <div className="text-center max-w-4xl mx-auto animate-on-scroll">
          <h2 className="text-hero font-brand mb-8">
            Book More. Schedule Less.{' '}
            <span className="text-blue-600">Grow Faster.</span>
          </h2>
          <p className="text-hero-sub font-brand mb-12">
            Take bookings online. Get paid automatically. Manage your team—all in one place. Built for tour operators, rental companies, museums, music venues, and any business that takes reservations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/signup')}
              size={null}
              className="btn-supplier-primary bg-blue-600 hover:bg-blue-700 text-white font-medium text-lg px-8 py-4 rounded-xl"
            >
              Request Demo <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              variant="outline"
              size={null}
              onClick={() => navigate('/login')}
              className="text-lg px-8 py-4 rounded-xl border-gray-200 hover:border-gray-300 hover:bg-gray-50"
            >
              Log In
            </Button>
          </div>
          <p className="text-caption font-brand mt-6">Free setup • No commitment required</p>
        </div>
      </section>


      {/* Combined Booking Software Section - Replacing both previous sections */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="badge-landing bg-purple-100 text-purple-700">
                Tours • Rentals • Venues • Museums • Experiences & More
              </span>
            </div>
            <h3 className="landing-section-title">
              Simple Booking Software That Does More
            </h3>
            <p className="landing-section-subtitle max-w-3xl mx-auto">
              Handle online bookings, payments, and operations. We built it for booking—then added the tools you actually need.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            {/* Card 1: Online Bookings & Payments */}
            <div className="landing-card hover:border-purple-200">
              <div className="icon-container-lg bg-gradient-to-br from-purple-500 to-pink-600">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h4 className="landing-card-title">Online Bookings & Payments</h4>
              <p className="landing-card-desc">
                Customers book 24/7. Payments go straight to your account. They get a shareable ticket with all their booking details.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Online Booking Tools</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Secure payments</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Shareable customer tickets</span>
                </li>
              </ul>
            </div>

            {/* Card 2: Operations & Team Tools */}
            <div className="landing-card hover:border-blue-200">
              <div className="icon-container-lg bg-gradient-to-br from-blue-500 to-cyan-600">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h4 className="landing-card-title">Operations & Team Tools</h4>
              <p className="landing-card-desc">
                Staff scheduling, digital waivers, role permissions, and team coordination. The operational stuff that keeps things running smooth.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">AI-assisted staff scheduling</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Digital waivers with verification</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Configure roles & permissions</span>
                </li>
              </ul>
            </div>

            {/* Card 3: Secure & Reliable */}
            <div className="landing-card hover:border-green-200">
              <div className="icon-container-lg bg-gradient-to-br from-green-500 to-emerald-600">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h4 className="landing-card-title">Secure & Reliable</h4>
              <p className="landing-card-desc">
                99.9% uptime so you're always open. Your data stays in your own database. Plus a simple CRM to help you grow.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">99.9% uptime guarantee</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Private database (your data, your control)</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Built-in customer management</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Cheeky footer text */}
          <div className="text-center mt-12">
            <p className="text-gray-600 text-lg">
              ...we even send thank you emails on your behalf.
            </p>
          </div>
        </div>
      </section>

      {/* Waiver Management Section - Move this EARLIER, remove old gradient callout */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="landing-section-title text-white">
              Digital Waivers That Actually Work
            </h3>
            <p className="landing-section-subtitle text-gray-300 max-w-3xl mx-auto">
              Protect your business with verified digital waivers. We collect device fingerprints and IP addresses—so you have proof if you ever need it.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="bg-blue-500 p-3 rounded-lg flex-shrink-0">
                  <Fingerprint className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="text-xl font-bold text-white mb-2">Device Verification</h4>
                  <p className="text-gray-300">
                    We automatically capture device fingerprints, IP addresses, and browser info when customers sign. Full audit trail for legal protection.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-green-500 p-3 rounded-lg flex-shrink-0">
                  <Lock className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="text-xl font-bold text-white mb-2">PIN-Based Tickets</h4>
                  <p className="text-gray-300">
                    Your guides get a unique PIN for each trip. Customers enter the PIN to access their ticket—guides instantly know who signed the waiver. Simple, secure.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-purple-500 p-3 rounded-lg flex-shrink-0">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="text-xl font-bold text-white mb-2">Custom Waiver Text</h4>
                  <p className="text-gray-300">
                    Write your own waiver text. Multi-signature support for groups. Customers sign on their phone before the trip.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-800 rounded-2xl p-8 border border-gray-700">
              <div className="bg-white rounded-xl p-6 mb-4">
                <div className="flex items-center justify-between mb-4">
                  <h5 className="text-gray-900 font-bold">Booking Waiver</h5>
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Verified</span>
                </div>
                <div className="space-y-3 text-sm text-gray-600">
                  <div className="flex justify-between">
                    <span>Device:</span>
                    <span className="font-mono">iPhone 15 Pro</span>
                  </div>
                  <div className="flex justify-between">
                    <span>IP Address:</span>
                    <span className="font-mono">203.0.113.45</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Location:</span>
                    <span className="font-mono">Austin, TX</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Timestamp:</span>
                    <span className="font-mono">11/11/25 4:32 PM</span>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-400 text-center">
                ✓ All verification data stored securely for legal compliance
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Ticket Feature */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="landing-section-title mb-6">
              Shareable Customer Tickets
            </h3>
            <p className="landing-section-subtitle max-w-3xl mx-auto">
              Give your customers everything they need in one simple link. No app downloads, no confusion.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Features List */}
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="bg-blue-500 p-3 rounded-lg flex-shrink-0">
                  <Check className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="landing-step-title">All Booking Details in One Place</h4>
                  <p className="text-gray-600">
                    Meeting location, time, asset name, team_member info, and emergency contacts—everything your customer needs to show up prepared.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-green-500 p-3 rounded-lg flex-shrink-0">
                  <Lock className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="landing-step-title">PIN-Protected Waiver Access</h4>
                  <p className="text-gray-600">
                    Customers sign waivers digitally using a secure PIN. TeamMembers instantly know who signed—no paperwork, no hassle.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-purple-500 p-3 rounded-lg flex-shrink-0">
                  <ArrowRight className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="landing-step-title">Instantly Shareable</h4>
                  <p className="text-gray-600">
                    Send via text, email, or any messaging app. Works on any device—no app required. Customers get the link, they're ready to go.
                  </p>
                </div>
              </div>
            </div>

            {/* Ticket Mockup */}
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl border-l-4 border-blue-600 p-8 max-w-md mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-6 pb-4 border-b">
                  <div className="flex items-center gap-2">
                    <div className="bg-blue-600 p-2 rounded-lg">
                      <Calendar className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Booking ID</p>
                      <p className="font-mono font-bold text-blue-600">BF-1CWQ4T</p>
                    </div>
                  </div>
                  <span className="text-xs bg-green-100 text-green-800 px-3 py-1 rounded-full font-semibold">Confirmed</span>
                </div>

                {/* Company Name */}
                <h3 className="font-bold text-2xl text-gray-900 mb-6">Austin ATV Rentals</h3>

                {/* Booking Details */}
                <div className="space-y-4 mb-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Asset</p>
                      <p className="font-semibold text-gray-900">Sunset Cruiser</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">TeamMember</p>
                      <p className="font-semibold text-gray-900">John S.</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Date</p>
                      <p className="font-semibold text-gray-900">Sat, Nov 15</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Time</p>
                      <p className="font-semibold text-gray-900">2:00 PM</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Duration</p>
                      <p className="font-semibold text-gray-900">3 hours</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Guests</p>
                      <p className="font-semibold text-gray-900">8 people</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Meeting Location</p>
                    <p className="font-semibold text-gray-900">Trailhead Parking</p>
                  </div>
                </div>

                {/* Emergency */}
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                  <p className="text-xs font-semibold text-orange-900 mb-1">Emergency Contact</p>
                  <p className="text-sm font-mono text-orange-700">+1 (512) 555-ASSET</p>
                </div>

                {/* Who's Signed */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <p className="text-xs font-semibold text-green-900 mb-2 uppercase tracking-wide">Who's Signed the Waiver</p>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-green-800">John Smith</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-green-800">Sarah Johnson</span>
                    </div>
                    <div className="flex items-center gap-2 opacity-50 mt-2 pt-2 border-t border-green-200">
                      <div className="w-4 h-4 border-2 border-gray-400 rounded"></div>
                      <span className="text-sm text-gray-600">6 more guests need to sign</span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="space-y-2">
                  <button className="btn-card-primary">
                    <Lock className="w-4 h-4" />
                    <span>Sign Waiver</span>
                    <span className="absolute right-4 text-xs opacity-75">2/8</span>
                  </button>
                  <button className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2">
                    <Star className="w-4 h-4 fill-white" />
                    Leave a Review
                  </button>
                </div>
                
                {/* Footer - Copy Link */}
                <div className="text-center mt-6 pt-4 border-t">
                  <button className="text-sm text-gray-500 hover:text-gray-700 font-medium flex items-center justify-center gap-2 mx-auto">
                    <Link2 className="w-4 h-4" />
                    Copy Ticket Link
                  </button>
                </div>
              </div>

              <div className="absolute -bottom-4 -right-4 bg-green-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                Works on any device
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Marketplace Section */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Text Content - Centered on top */}
          <div className="text-center mb-12">
            <div className="inline-block mb-4">
              <span className="bg-blue-100 text-blue-700 px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold">
                Expand Your Reach
              </span>
            </div>
            <h3 className="landing-section-title mb-4">
              Get Listed on Our Marketplace
            </h3>
            <p className="text-gray-600 text-lg mb-6 max-w-2xl mx-auto">
              Reach thousands of travelers searching for experiences like yours. Your services get discovered by customers actively looking to book.
            </p>
            <div className="flex flex-wrap justify-center gap-x-8 gap-y-3 mb-8">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <span className="text-gray-700">Instant visibility</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <span className="text-gray-700">Professional profile</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <span className="text-gray-700">SEO-optimized</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <span className="text-gray-700">Included free</span>
              </div>
            </div>
            <Button 
              onClick={() => navigate('/')}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-3 rounded-xl"
            >
              View Marketplace <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
          
          {/* Visual - Browser Window Mockup below */}
          <div className="relative max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden">
              <div className="bg-gray-100 px-4 py-2 flex items-center gap-2 border-b">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400"></div>
                </div>
                <div className="flex-1 text-center">
                  <span className="text-xs text-gray-500">bookingflows.com</span>
                </div>
              </div>
              <div className="p-4 bg-gray-50">
                {/* Marketplace Header */}
                <div className="bg-white rounded-lg p-3 mb-3 border border-gray-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <img src="/logo-icon.png" alt="BookingFlows" className="w-6 h-6 rounded" />
                      <span className="font-semibold text-sm text-gray-900">BookingFlows</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-6 w-24 bg-gray-100 rounded-full"></div>
                    </div>
                  </div>
                </div>
                {/* Experience Cards Grid - Taller with photos */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-white rounded-lg overflow-hidden border border-gray-100">
                    <div className="h-32 bg-cover bg-center" style={{backgroundImage: "url('https://images.unsplash.com/photo-1664532962042-07b2f58bdd8e?w=400')"}}></div>
                    <div className="p-2">
                      <p className="font-medium text-xs text-gray-900 truncate">Sunset Boat Tour</p>
                      <p className="text-xs text-gray-500">Austin, TX</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs font-semibold text-blue-600">$89</span>
                        <div className="flex items-center gap-0.5">
                          <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                          <span className="text-xs">4.9</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg overflow-hidden border border-gray-100">
                    <div className="h-32 bg-cover bg-center" style={{backgroundImage: "url('https://images.unsplash.com/photo-1736147936423-787f57fbfd4c?w=400')"}}></div>
                    <div className="p-2">
                      <p className="font-medium text-xs text-gray-900 truncate">ATV Adventure</p>
                      <p className="text-xs text-gray-500">Hill Country</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs font-semibold text-blue-600">$120</span>
                        <div className="flex items-center gap-0.5">
                          <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                          <span className="text-xs">4.8</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg overflow-hidden border border-gray-100">
                    <div className="h-32 bg-cover bg-center" style={{backgroundImage: "url('https://images.unsplash.com/photo-1632854269219-4ec2553b913c?w=400')"}}></div>
                    <div className="p-2">
                      <p className="font-medium text-xs text-gray-900 truncate">Wine Tasting</p>
                      <p className="text-xs text-gray-500">Fredericksburg</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs font-semibold text-blue-600">$75</span>
                        <div className="flex items-center gap-0.5">
                          <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                          <span className="text-xs">4.7</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-3 -right-3 bg-blue-600 text-white px-3 py-1.5 rounded-full text-xs font-semibold shadow-lg">
              Free Listing
            </div>
          </div>
        </div>
      </section>

      {/* Embeddable Booking Widget Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Text Content - Centered on top */}
          <div className="text-center mb-12">
            <div className="inline-block mb-4">
              <span className="bg-purple-100 text-purple-700 px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold">
                Your Website
              </span>
            </div>
            <h3 className="landing-section-title mb-4">
              Embeddable Booking Widget
            </h3>
            <p className="text-gray-600 text-lg mb-6 max-w-2xl mx-auto">
              Add our booking widget to your existing website. Customers book directly from your site—no redirects, no friction.
            </p>
            <div className="flex flex-wrap justify-center gap-x-8 gap-y-3 mb-8">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-purple-600 flex-shrink-0" />
                <span className="text-gray-700">Copy & paste install</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-purple-600 flex-shrink-0" />
                <span className="text-gray-700">Brand colors</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-purple-600 flex-shrink-0" />
                <span className="text-gray-700">Mobile-responsive</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-purple-600 flex-shrink-0" />
                <span className="text-gray-700">Real-time sync</span>
              </div>
            </div>
            <div className="bg-gray-900 rounded-xl p-4 font-mono text-sm inline-block">
              <p className="text-gray-400 mb-1">{`<!-- Add to your website -->`}</p>
              <p className="text-green-400">{`<script src="bookingflows.com/widget.js"></script>`}</p>
            </div>
          </div>
          
          {/* Two Widget Mockups Side by Side */}
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Services Grid View - with browser frame */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden">
              {/* Browser bar */}
              <div className="bg-gray-100 px-4 py-2 flex items-center gap-2 border-b">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400"></div>
                </div>
                <div className="flex-1 text-center">
                  <span className="text-xs text-gray-500">yoursite.com/book</span>
                </div>
              </div>
              <div className="p-6 bg-white">
                {/* Header like real widget */}
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">Your Company</h3>
                  <p className="text-sm text-gray-500">Choose your experience</p>
                </div>
                
                {/* Service Cards Grid - Like real widget with photos */}
                <div className="grid grid-cols-2 gap-3">
                  {/* Service Card 1 - Sunset Cruise */}
                  <div className="relative rounded-2xl overflow-hidden aspect-[4/5] cursor-pointer shadow-md">
                    <div className="absolute inset-0 bg-cover bg-center" style={{backgroundImage: "url('https://images.unsplash.com/photo-1669430299454-2677085fa0a3?w=400')"}}></div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
                      <div className="flex items-baseline gap-1 mb-1 flex-wrap">
                        <span className="text-sm font-bold leading-tight">Sunset Sail Cruise</span>
                        <span className="border border-white/80 px-1.5 py-0.5 rounded text-xs font-semibold bg-black/30">$125</span>
                      </div>
                      <p className="text-[10px] opacity-80 mb-2">2 hours • Capacity: 12</p>
                      <button className="bg-blue-500 text-white text-xs font-semibold py-1.5 rounded-lg w-full">
                        Select date
                      </button>
                    </div>
                  </div>
                  
                  {/* Service Card 2 - Fishing Charter */}
                  <div className="relative rounded-2xl overflow-hidden aspect-[4/5] cursor-pointer shadow-md">
                    <div className="absolute inset-0 bg-cover bg-center" style={{backgroundImage: "url('https://images.unsplash.com/photo-1625183656263-171183307b15?w=400')"}}></div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
                      <div className="flex items-baseline gap-1 mb-1 flex-wrap">
                        <span className="text-sm font-bold leading-tight">Deep Sea Fishing</span>
                        <span className="border border-white/80 px-1.5 py-0.5 rounded text-xs font-semibold bg-black/30">$395</span>
                      </div>
                      <p className="text-[10px] opacity-80 mb-2">6 hours • Capacity: 8</p>
                      <button className="bg-blue-500 text-white text-xs font-semibold py-1.5 rounded-lg w-full">
                        Select date
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Calendar View - with browser frame */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden">
              {/* Browser bar */}
              <div className="bg-gray-100 px-4 py-2 flex items-center gap-2 border-b">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400"></div>
                </div>
                <div className="flex-1 text-center">
                  <span className="text-xs text-gray-500">yoursite.com/book</span>
                </div>
              </div>
              <div className="p-6 bg-white">
                {/* Header */}
                <h3 className="text-xl font-bold text-gray-900 mb-4">Book Your Experience</h3>
                
                {/* Select Date Row */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-1">
                    <span className="text-sm font-medium text-gray-700">Select Date</span>
                    <span className="text-red-500">*</span>
                  </div>
                  <div className="flex items-center gap-1 text-green-600 text-sm">
                    <Check className="w-4 h-4" />
                    <span>Availability checked</span>
                  </div>
                </div>
                
                {/* Month Header */}
                <div className="flex items-center justify-between mb-4">
                  <ArrowRight className="w-4 h-4 text-gray-400 rotate-180" />
                  <span className="font-semibold text-gray-900">January 2026</span>
                  <ArrowRight className="w-4 h-4 text-gray-400" />
                </div>
                
                {/* Calendar Grid */}
                <div className="bg-gray-50 rounded-xl p-3">
                  {/* Day Headers */}
                  <div className="grid grid-cols-7 gap-1 mb-2 text-center">
                    {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
                      <span key={day} className="text-xs font-medium text-gray-500 py-1">{day}</span>
                    ))}
                  </div>
                  
                  {/* Calendar Days */}
                  <div className="grid grid-cols-7 gap-1">
                    {/* Empty slots for alignment */}
                    {[...Array(3)].map((_, i) => (
                      <div key={`empty-${i}`} className="aspect-square"></div>
                    ))}
                    {/* Days 1-31 */}
                    {[...Array(31)].map((_, i) => {
                      const day = i + 1;
                      const isAvailable = day >= 21;
                      const isToday = day === 21;
                      return (
                        <div
                          key={day}
                          className={`aspect-square rounded-lg flex items-center justify-center text-xs font-medium ${
                            isAvailable
                              ? 'bg-blue-500 text-white'
                              : 'bg-gray-100 text-gray-400'
                          } ${isToday ? 'ring-2 ring-blue-300' : ''}`}
                        >
                          {day}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CRM Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-block mb-4">
                <span className="bg-green-100 text-green-700 px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold">
                  Customer Management
                </span>
              </div>
              <h3 className="landing-section-title mb-4">
                Customer Database
              </h3>
              <p className="text-gray-600 text-lg mb-6">
                Every booking builds your customer list. See booking history and contact details—all in one place.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">Complete booking history per customer</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">Contact details & preferences</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">Automated thank you emails</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">Export data anytime</span>
                </li>
              </ul>
            </div>
            <div className="relative">
              <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <h4 className="font-semibold text-gray-900">Recent Customers</h4>
                  <span className="text-sm text-blue-600 font-medium">View All</span>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 font-semibold text-sm">JS</span>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">John Smith</p>
                      <p className="text-sm text-gray-500">3 bookings • Last: Nov 15</p>
                    </div>
                    <span className="text-green-600 font-semibold">$450</span>
                  </div>
                  <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                      <span className="text-purple-600 font-semibold text-sm">SJ</span>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">Sarah Johnson</p>
                      <p className="text-sm text-gray-500">5 bookings • Last: Nov 12</p>
                    </div>
                    <span className="text-green-600 font-semibold">$825</span>
                  </div>
                  <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-green-600 font-semibold text-sm">MD</span>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">Mike Davis</p>
                      <p className="text-sm text-gray-500">1 booking • Last: Nov 10</p>
                    </div>
                    <span className="text-green-600 font-semibold">$150</span>
                  </div>
                </div>
                <div className="mt-6 pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Total Customers</span>
                    <span className="font-semibold text-gray-900">1,234</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* QR Scanner Section */}
      <section className="py-24 bg-gradient-to-br from-purple-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="order-2 md:order-1">
              <div className="flex gap-6 justify-center items-start">
                {/* Ticket with QR Code */}
                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-4 w-48 flex-shrink-0">
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b">
                    <div className="bg-blue-600 p-1.5 rounded">
                      <Calendar className="w-3 h-3 text-white" />
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500">Booking ID</p>
                      <p className="text-xs font-bold text-blue-600">BF-ABC123</p>
                    </div>
                  </div>
                  <p className="font-bold text-sm text-gray-900 mb-1">BOATX</p>
                  <p className="text-xs text-gray-500 mb-3">Sat, Nov 29 • 2:00 PM</p>
                  {/* QR Code */}
                  <div className="bg-gray-50 p-3 rounded-xl mb-2">
                    <QrCode className="w-full h-auto text-gray-900" />
                  </div>
                  <p className="text-[10px] text-center text-gray-400">Scan to check in</p>
                </div>

                {/* Phone Scanner */}
                <div className="relative max-w-[200px]">
                  <div className="bg-gray-900 rounded-[2rem] p-2 shadow-2xl">
                    <div className="bg-white rounded-[1.5rem] overflow-hidden">
                      <div className="bg-purple-600 px-4 py-2">
                        <p className="text-white font-semibold text-sm text-center">Scan QR Code</p>
                      </div>
                      <div className="p-3">
                        {/* Scanner Viewfinder with QR */}
                        <div className="relative aspect-square bg-gray-900 rounded-xl mb-3 overflow-hidden">
                          {/* Corner brackets */}
                          <div className="absolute top-2 left-2 w-4 h-4 border-t-2 border-l-2 border-purple-400"></div>
                          <div className="absolute top-2 right-2 w-4 h-4 border-t-2 border-r-2 border-purple-400"></div>
                          <div className="absolute bottom-2 left-2 w-4 h-4 border-b-2 border-l-2 border-purple-400"></div>
                          <div className="absolute bottom-2 right-2 w-4 h-4 border-b-2 border-r-2 border-purple-400"></div>
                          {/* Scan line */}
                          <div className="absolute left-2 right-2 h-0.5 bg-purple-500 top-1/3 animate-pulse shadow-[0_0_10px_rgba(168,85,247,0.8)]"></div>
                          {/* QR Code being scanned */}
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-16 h-16 bg-white p-1.5 rounded shadow-lg">
                              <QrCode className="w-full h-full text-gray-900" />
                            </div>
                          </div>
                        </div>
                        {/* Success State */}
                        <div className="bg-green-50 border border-green-200 rounded-lg p-2 text-center">
                          <Check className="w-5 h-5 text-green-600 mx-auto mb-1" />
                          <p className="font-semibold text-green-800 text-xs">Verified!</p>
                          <p className="text-[10px] text-green-600">John Smith • 4 guests</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <div className="inline-block mb-4">
                <span className="bg-purple-100 text-purple-700 px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold">
                  QR Check-In
                </span>
              </div>
              <h3 className="landing-section-title mb-4">
                Turn Any Device Into a Scanner
              </h3>
              <p className="text-gray-600 text-lg mb-6">
                No special hardware needed. Your phone becomes a professional check-in system. Scan customer QR codes and verify bookings instantly.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-purple-600 flex-shrink-0" />
                  <span className="text-gray-700">Works on any smartphone or tablet</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-purple-600 flex-shrink-0" />
                  <span className="text-gray-700">Instant guest verification</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-purple-600 flex-shrink-0" />
                  <span className="text-gray-700">See booking details immediately</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-purple-600 flex-shrink-0" />
                  <span className="text-gray-700">Track check-ins in real-time</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* White Label Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-block mb-4">
                <span className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold">
                  Your Brand
                </span>
              </div>
              <h3 className="landing-section-title mb-4">
                White Label Option
              </h3>
              <p className="text-gray-600 text-lg mb-6">
                Make it yours. Remove our branding completely and present the booking system as your own. Your customers see your brand, not ours.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-gray-600 flex-shrink-0" />
                  <span className="text-gray-700">Your logo on all customer touchpoints</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-gray-600 flex-shrink-0" />
                  <span className="text-gray-700">Custom colors to match your brand</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-gray-600 flex-shrink-0" />
                  <span className="text-gray-700">No "Powered by" watermarks</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-gray-600 flex-shrink-0" />
                  <span className="text-gray-700">100% free—included with your account</span>
                </li>
              </ul>
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <p className="text-green-800 font-medium">
                  Free of charge. On us. No hidden fees.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="space-y-4">
                {/* Before */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs text-gray-500 mb-2 font-medium">BEFORE</p>
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 bg-blue-600 rounded-lg"></div>
                      <span className="font-semibold text-gray-400">BookingFlows</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-2 bg-gray-100 rounded w-1/2"></div>
                    <div className="mt-3 pt-3 border-t text-xs text-gray-400 text-center">
                      Powered by BookingFlows
                    </div>
                  </div>
                </div>
                {/* After */}
                <div className="bg-blue-50 rounded-xl p-4 border-2 border-blue-200">
                  <p className="text-xs text-blue-600 mb-2 font-medium">AFTER (WHITE LABEL)</p>
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center text-white font-bold text-xs">YB</div>
                      <span className="font-semibold text-gray-900">Your Brand</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-2 bg-gray-100 rounded w-1/2"></div>
                    <div className="mt-3 pt-3 border-t text-xs text-gray-900 text-center font-medium">
                      © Your Company Name
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Payments & Global - Combined Stripe + Multi-Currency */}
      <section className="py-24 bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 mb-6">
              {/* Stripe Logo */}
              <div className="bg-white rounded-xl p-3">
                <svg className="w-16 h-7" viewBox="0 0 60 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" clipRule="evenodd" d="M60 12.8c0-4.28-2.07-7.66-6.03-7.66-3.98 0-6.38 3.38-6.38 7.63 0 5.04 2.84 7.58 6.92 7.58 1.99 0 3.49-.45 4.63-1.09v-3.33c-1.14.57-2.45.93-4.11.93-1.63 0-3.07-.57-3.25-2.56h8.19c0-.22.03-1.08.03-1.5zm-8.19-1.59c0-1.88 1.15-2.67 2.19-2.67 1.02 0 2.1.79 2.1 2.67h-4.29zM41.3 5.14c-1.64 0-2.7.77-3.28 1.3l-.22-1.04H34v19.8l4.22-.9v-4.8c.6.44 1.49 1.06 2.97 1.06 3 0 5.74-2.42 5.74-7.77 0-4.9-2.78-7.65-5.63-7.65zm-.99 11.76c-.99 0-1.57-.36-1.98-.8v-6.3c.44-.48 1.04-.82 1.98-.82 1.52 0 2.57 1.7 2.57 3.94 0 2.3-1.03 3.98-2.57 3.98zM27.65 4.3l4.24-.9V0l-4.24.9v3.4zm0 .98h4.24v14.6h-4.24V5.28zM22.94 6.6l-.26-1.32h-3.63v14.6h4.22v-9.9c1-.3 2.68-1.06 3.2-.88V5.28c-.54-.2-2.51-.57-3.53 1.32zM13.68 1.38L9.56 2.25v13.35c0 2.47 1.85 4.28 4.3 4.28 1.36 0 2.35-.25 2.9-.55v-3.45c-.53.22-3.17 1-3.17-1.5V8.93h3.17V5.28h-3.17l.09-3.9zM4.16 9.84c0-.65.54-.9 1.42-.9 1.27 0 2.87.39 4.14 1.08V6.2c-1.39-.55-2.76-.77-4.14-.77C2.25 5.43 0 7.1 0 10.02c0 4.5 6.2 3.78 6.2 5.72 0 .77-.67 1.02-1.6 1.02-1.39 0-3.17-.57-4.58-1.35v3.82c1.56.67 3.14.96 4.58.96 3.35 0 5.58-1.66 5.58-4.72 0-4.86-6.22-4-6.22-5.63z" fill="#635BFF"/>
                </svg>
              </div>
            </div>
            <h3 className="text-3xl sm:text-4xl font-bold mb-4">
              Payments Powered by Stripe
            </h3>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">
              Secure payments, instant payouts, and multi-currency support—all handled by one of the most trusted financial platforms in the world.
            </p>
          </div>
          
          {/* Features Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/10">
              <div className="bg-green-500/20 w-10 h-10 rounded-lg flex items-center justify-center mb-3">
                <Shield className="w-5 h-5 text-green-400" />
              </div>
              <h4 className="font-semibold mb-1">Secure Payments</h4>
              <p className="text-sm text-white/70">Bank-level encryption for every transaction</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/10">
              <div className="bg-blue-500/20 w-10 h-10 rounded-lg flex items-center justify-center mb-3">
                <Zap className="w-5 h-5 text-blue-400" />
              </div>
              <h4 className="font-semibold mb-1">Instant Payouts</h4>
              <p className="text-sm text-white/70">Money in your account within days</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/10">
              <div className="bg-purple-500/20 w-10 h-10 rounded-lg flex items-center justify-center mb-3">
                <Globe className="w-5 h-5 text-purple-400" />
              </div>
              <h4 className="font-semibold mb-1">Multi-Currency</h4>
              <p className="text-sm text-white/70">Offer customers multiple currency options</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/10">
              <div className="bg-orange-500/20 w-10 h-10 rounded-lg flex items-center justify-center mb-3">
                <FileText className="w-5 h-5 text-orange-400" />
              </div>
              <h4 className="font-semibold mb-1">Tax Forms (1099)</h4>
              <p className="text-sm text-white/70">Automated tax reporting handled for you</p>
            </div>
          </div>
          
          {/* Trust badges */}
          <div className="flex flex-wrap justify-center gap-6 text-center text-sm text-white/60">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4" />
              <span>PCI Compliant</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              <span>256-bit SSL</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              <span>Millions of Businesses Trust Stripe</span>
            </div>
          </div>
        </div>
      </section>

      {/* Integrations Section - More Software-y */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="landing-section-title mb-4">
              We Integrate With
            </h3>
            <p className="landing-section-subtitle max-w-2xl mx-auto">
              Sync your availability and bookings across platforms automatically.
            </p>
          </div>

          {/* Integration Visual */}
          <div className="max-w-4xl mx-auto">
            <div className="relative flex items-center justify-center gap-4 md:gap-8">
              {/* Left Platform - GetYourGuide */}
              <div className="flex flex-col items-center">
                <div className="bg-white rounded-2xl p-4 border-2 border-green-300 shadow-lg hover:shadow-xl transition-all relative">
                  <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">LIVE</div>
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden">
                    <img src="/images/integrations/gyg-icon.jpg" alt="GetYourGuide" className="w-full h-full object-cover" />
                  </div>
                </div>
                <span className="mt-2 text-sm font-medium text-gray-700">GetYourGuide</span>
              </div>

              {/* Connection Line with Arrow */}
              <div className="flex items-center">
                <div className="w-6 md:w-12 h-0.5 bg-gradient-to-r from-green-400 to-blue-400"></div>
                <div className="bg-blue-500 rounded-full p-1">
                  <ArrowRight className="w-3 h-3 text-white" />
                </div>
              </div>

              {/* Center - BookingFlows */}
              <div className="flex flex-col items-center">
                <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-4 shadow-xl border-2 border-blue-400 relative">
                  <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">SYNC</div>
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden bg-white flex items-center justify-center">
                    <img src="/logo-icon.png" alt="BookingFlows" className="w-12 h-12 md:w-16 md:h-16 object-contain" />
                  </div>
                </div>
                <span className="mt-2 text-sm font-bold text-blue-600">BookingFlows</span>
              </div>

              {/* Connection Line with Arrow */}
              <div className="flex items-center">
                <div className="bg-blue-500 rounded-full p-1">
                  <ArrowRight className="w-3 h-3 text-white rotate-180" />
                </div>
                <div className="w-6 md:w-12 h-0.5 bg-gradient-to-l from-green-400 to-blue-400"></div>
              </div>

              {/* Right Platform - LetsBatch */}
              <div className="flex flex-col items-center">
                <div className="bg-white rounded-2xl p-4 border-2 border-green-300 shadow-lg hover:shadow-xl transition-all relative">
                  <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">LIVE</div>
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden bg-black p-1 flex items-center justify-center">
                    <img src="/images/integrations/letsbatch-icon.png" alt="LetsBatch" className="w-full h-full object-contain" />
                  </div>
                </div>
                <span className="mt-2 text-sm font-medium text-gray-700">LetsBatch</span>
              </div>
            </div>

            {/* Coming Soon - Viator */}
            <div className="flex justify-center mt-8">
              <div className="flex items-center gap-3 bg-white border border-dashed border-gray-300 rounded-full px-5 py-2.5">
                <div className="w-8 h-8 rounded-lg overflow-hidden bg-white flex items-center justify-center">
                  <img src="/images/integrations/viator-icon.png" alt="Viator" className="w-full h-full object-contain" />
                </div>
                <span className="text-sm text-gray-600">Viator</span>
                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">Coming Soon</span>
              </div>
            </div>

            {/* Feature Pills */}
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              <div className="bg-white border border-gray-200 rounded-full px-4 py-2 text-sm flex items-center gap-2 shadow-sm">
                <Check className="w-4 h-4 text-green-500" />
                <span className="text-gray-700">Real-time availability sync</span>
              </div>
              <div className="bg-white border border-gray-200 rounded-full px-4 py-2 text-sm flex items-center gap-2 shadow-sm">
                <Check className="w-4 h-4 text-green-500" />
                <span className="text-gray-700">Automatic booking updates</span>
              </div>
              <div className="bg-white border border-gray-200 rounded-full px-4 py-2 text-sm flex items-center gap-2 shadow-sm">
                <Check className="w-4 h-4 text-green-500" />
                <span className="text-gray-700">No double bookings</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* API Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="landing-section-title mb-6">
              Developer-Friendly API
            </h3>
            <p className="landing-section-subtitle max-w-3xl mx-auto">
              Integrate BookingFlows with your existing software. Full REST API and comprehensive documentation.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto items-start justify-center px-2 sm:px-0">
            {/* Terminal Mock */}
            <div className="bg-gray-900 rounded-xl p-4 sm:p-6 shadow-2xl mx-auto w-full max-w-[320px] sm:max-w-lg md:max-w-none overflow-hidden">
              <div className="flex items-center gap-2 mb-4">
                <div className="flex gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                </div>
                <span className="text-xs text-gray-400 ml-2">api_request.sh</span>
              </div>
              <div className="font-mono text-xs sm:text-sm space-y-2 overflow-x-auto">
                <div className="text-gray-400 whitespace-nowrap">$ <span className="text-green-400">curl</span> <span className="text-blue-400 break-all sm:break-normal">https://api.bookingflows.com/bookings</span></div>
                <div className="text-gray-300 pl-2">{`{`}</div>
                <div className="text-gray-300 pl-4">"<span className="text-yellow-400">status</span>": "<span className="text-green-400">success</span>",</div>
                <div className="text-gray-300 pl-4">"<span className="text-yellow-400">bookings</span>": [</div>
                <div className="text-gray-300 pl-6">{`{`}</div>
                <div className="text-gray-300 pl-8">"<span className="text-yellow-400">id</span>": "<span className="text-purple-400">bk_abc123</span>",</div>
                <div className="text-gray-300 pl-8">"<span className="text-yellow-400">customer</span>": "<span className="text-blue-300">John Smith</span>",</div>
                <div className="text-gray-300 pl-8">"<span className="text-yellow-400">date</span>": "<span className="text-blue-300">2025-11-20</span>",</div>
                <div className="text-gray-300 pl-8">"<span className="text-yellow-400">status</span>": "<span className="text-green-400">confirmed</span>"</div>
                <div className="text-gray-300 pl-6">{`}`}</div>
                <div className="text-gray-300 pl-4">]</div>
                <div className="text-gray-300 pl-2">{`}`}</div>
                <div className="text-green-400 mt-4">✓ <span className="text-gray-400">200 OK</span></div>
              </div>
            </div>

            {/* API Features List */}
            <div className="space-y-6">
              <div>
                <h4 className="landing-card-title">Simple REST Endpoints</h4>
                <p className="text-gray-600 mb-6">
                  Access all features programmatically. Create bookings, manage schedules, sync data—clean JSON responses.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 bg-white p-4 rounded-lg border border-gray-200">
                    <code className="text-sm font-mono text-blue-600 font-semibold">GET</code>
                    <span className="text-gray-700">/api/bookings</span>
                  </div>
                  <div className="flex items-center gap-3 bg-white p-4 rounded-lg border border-gray-200">
                    <code className="text-sm font-mono text-green-600 font-semibold">POST</code>
                    <span className="text-gray-700">/api/bookings</span>
                  </div>
                  <div className="flex items-center gap-3 bg-white p-4 rounded-lg border border-gray-200">
                    <code className="text-sm font-mono text-orange-600 font-semibold">PUT</code>
                    <span className="text-gray-700">/api/team/{`{id}`}</span>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
                <h5 className="font-semibold text-gray-900 mb-2">Built for Developers</h5>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <span>1,000 requests/hour per API key</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <span>Rate limiting & usage analytics</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <span>No tech team needed for basic setup</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security & Compliance Section */}
      <section className="py-24 bg-gradient-to-b from-gray-900 to-gray-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="bg-green-500/20 text-green-400 px-4 py-2 rounded-full text-sm font-semibold border border-green-500/30">
                Enterprise-Grade Security
              </span>
            </div>
            <h3 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Your Data, Protected
            </h3>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Built for businesses that take privacy seriously. We're working toward the highest compliance standards.
            </p>
          </div>

          {/* Security Features Grid */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {/* Encryption */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-green-500/30 transition-all">
              <div className="w-14 h-14 bg-green-500/20 rounded-xl flex items-center justify-center mb-6">
                <Lock className="w-7 h-7 text-green-400" />
              </div>
              <h4 className="text-xl font-semibold text-white mb-3">End-to-End Encryption</h4>
              <p className="text-gray-400 leading-relaxed">
                <span className="text-green-400 font-medium">AES-256 encryption at rest</span> and <span className="text-green-400 font-medium">TLS 1.3 in transit</span>. Your customer data, payment info, and signed waivers are protected with military-grade encryption.
              </p>
            </div>

            {/* EU Hosting */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-blue-500/30 transition-all">
              <div className="w-14 h-14 bg-blue-500/20 rounded-xl flex items-center justify-center mb-6">
                <Shield className="w-7 h-7 text-blue-400" />
              </div>
              <h4 className="text-xl font-semibold text-white mb-3">EU Data Residency</h4>
              <p className="text-gray-400 leading-relaxed">
                Database and APIs hosted in <span className="text-blue-400 font-medium">Frankfurt, Germany</span>—one of the world's strictest data privacy jurisdictions. Your data never leaves the EU.
              </p>
            </div>

            {/* Compliance */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-purple-500/30 transition-all">
              <div className="w-14 h-14 bg-purple-500/20 rounded-xl flex items-center justify-center mb-6">
                <FileText className="w-7 h-7 text-purple-400" />
              </div>
              <h4 className="text-xl font-semibold text-white mb-3">Compliance-Ready</h4>
              <p className="text-gray-400 leading-relaxed">
                Building toward <span className="text-purple-400 font-medium">GDPR</span>, <span className="text-purple-400 font-medium">CCPA</span>, and <span className="text-purple-400 font-medium">SOC 2</span> compliance standards. We take privacy regulations seriously.
              </p>
            </div>
          </div>

          {/* Trust Badges */}
          <div className="flex flex-wrap justify-center items-center gap-6 md:gap-12 pt-8 border-t border-white/10">
            <div className="flex items-center gap-3 text-gray-400">
              <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                <Lock className="w-5 h-5 text-green-400" />
              </div>
              <div className="text-left">
                <div className="text-sm font-medium text-white">256-bit SSL</div>
                <div className="text-xs text-gray-500">Encrypted Connection</div>
              </div>
            </div>
            
            <div className="flex items-center gap-3 text-gray-400">
              <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-blue-400" />
              </div>
              <div className="text-left">
                <div className="text-sm font-medium text-white">GDPR Ready</div>
                <div className="text-xs text-gray-500">EU Compliant</div>
              </div>
            </div>
            
            <div className="flex items-center gap-3 text-gray-400">
              <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                <Activity className="w-5 h-5 text-purple-400" />
              </div>
              <div className="text-left">
                <div className="text-sm font-medium text-white">99.9% Uptime</div>
                <div className="text-xs text-gray-500">Enterprise SLA</div>
              </div>
            </div>
            
            <div className="flex items-center gap-3 text-gray-400">
              <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                <Fingerprint className="w-5 h-5 text-orange-400" />
              </div>
              <div className="text-left">
                <div className="text-sm font-medium text-white">SOC 2 Roadmap</div>
                <div className="text-xs text-gray-500">In Progress</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-block mb-4">
              <span className="bg-green-100 text-green-700 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-semibold">
                Simple Pricing
              </span>
            </div>
            <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              We Only Make Money When You Do
            </h3>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              No monthly fees. No setup costs. Just a small fee added to each booking—paid by your customer, not you.
            </p>
          </div>

          <div className="max-w-sm mx-auto">
            {/* Pricing Breakdown Card */}
            <Card className="bg-white border border-gray-200 shadow-xl rounded-2xl overflow-hidden">
              <CardContent className="p-0">
                {/* Receipt Style Breakdown */}
                <div className="px-6 py-6">
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Service</span>
                      <span className="font-medium text-gray-900">$350.00</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Add-ons</span>
                      <span className="font-medium text-gray-900">+$50.00</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Tax</span>
                      <span className="font-medium text-gray-900">+$33.00</span>
                    </div>
                    <div className="flex justify-between items-center pt-3 border-t border-dashed border-gray-200">
                      <span className="text-gray-700">BookingFlows Fee (3%)</span>
                      <span className="font-medium text-blue-600">+$13.00</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Stripe Processing*</span>
                      <span className="font-medium text-purple-600">+$13.32</span>
                    </div>
                  </div>
                  
                  {/* Total */}
                  <div className="mt-4 pt-4 border-t-2 border-gray-900">
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-gray-900 text-lg">Customer Pays</span>
                      <span className="font-bold text-gray-900 text-xl">$459.32</span>
                    </div>
                  </div>
                  
                  {/* You Get */}
                  <div className="mt-4 bg-green-50 rounded-xl p-4 border border-green-200">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-green-800">You Get</span>
                      <span className="font-bold text-green-700 text-xl">$433.00</span>
                    </div>
                    <p className="text-xs text-green-600 mt-1">Service + Add-ons + Tax</p>
                  </div>
                  
                  {/* Stripe footnote */}
                  <p className="text-xs text-gray-400 mt-4">
                    *Stripe charges 2.9% + 30¢ per transaction
                  </p>
                </div>
                
                {/* CTA */}
                <div className="px-6 pb-6">
                  <Button 
                    className="btn-supplier-primary w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-4 rounded-xl"
                    onClick={() => navigate('/signup')}
                  >
                    Get Started <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            <p className="text-center text-sm text-gray-500 mt-4">
              No monthly fees • No setup costs • Cancel anytime
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900 py-32 overflow-hidden">
        {/* Subtle Background Pattern - NOT blue to avoid clash */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-96 h-96 bg-purple-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-indigo-500 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-4xl mx-auto text-center px-4 relative z-10">
          <h3 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Ready to Get Started?
          </h3>
          <p className="text-2xl md:text-2xl text-gray-300 mb-12 max-w-2xl mx-auto leading-relaxed">
            Start taking bookings online today.
          </p>
          <Button 
            onClick={() => navigate('/signup')}
            className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white text-xl px-12 py-6 rounded-lg font-medium shadow-2xl shadow-blue-500/30 transform hover:scale-105 transition-all duration-300"
          >
            Get Started <ArrowRight className="ml-3 w-6 h-6" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img 
                  src="/logo-icon.png" 
                  alt="BookingFlows" 
                  className="w-9 h-9 rounded-lg object-cover"
                />
                <h4 className="text-white font-bold text-lg">BookingFlows</h4>
              </div>
              <p className="text-sm">
                Online booking software for tours, venues, experiences, and any business that takes reservations.
              </p>
            </div>
            <div>
              <h5 className="text-white font-semibold mb-4">Product</h5>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><button onClick={() => navigate('/login')} className="hover:text-white transition-colors">Login</button></li>
              </ul>
            </div>
            <div>
              <h5 className="text-white font-semibold mb-4">Support</h5>
              <ul className="space-y-2 text-sm">
                <li><a href="mailto:support@bookingflows.zendesk.com" className="hover:text-white transition-colors">Contact Us</a></li>
              </ul>
            </div>
            <div>
              <h5 className="text-white font-semibold mb-4">Legal</h5>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/privacy')} className="hover:text-white transition-colors">Privacy Policy</button></li>
                <li><button onClick={() => navigate('/terms')} className="hover:text-white transition-colors">Terms of Service</button></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm">
            <p>&copy; {new Date().getFullYear()} BookingFlows. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SupplierLandingPage;
